"use client";

// Mixed media carousel for centre pages: images and YouTube embeds share one
// scroll-snap track with arrows and dot indicators. Items are plain strings —
// a YouTube watch/short/embed URL becomes an iframe, anything else an <img>.
import { useCallback, useEffect, useRef, useState } from "react";

const YT = /(?:youtube\.com\/(?:watch\?.*?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/;

export function youtubeId(src: string): string | null {
  return YT.exec(src)?.[1] ?? null;
}

/** How long the automatic cycle stands down after the reader touches it. */
const RESUME_AFTER_MS = 12_000;

export function MediaCarousel({
  items,
  alt = "",
  className = "",
}: {
  items: string[];
  alt?: string;
  className?: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  // Auto-advance yields to the reader rather than stopping for good. It used
  // to latch off at the first interaction, so one press of an arrow — the
  // control the carousel itself offers — turned the gallery manual for the
  // rest of the visit. Now an interaction only holds it, and it picks back up
  // once the reader has been still for a while: the arrows stay available
  // without them costing the automatic cycle.
  const heldUntil = useRef(0);

  const goTo = useCallback((i: number) => {
    const el = track.current;
    if (!el) return;
    // An arrow press is a reader taking over; hold the cycle the same way a
    // swipe or a key does. The arrows call this, and they sit outside the
    // track's own listeners, so without this the timer would advance a slide
    // out from under someone stepping through by hand.
    heldUntil.current = Date.now() + RESUME_AFTER_MS;
    const clamped = Math.max(0, Math.min(items.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  }, [items.length]);

  const onScroll = () => {
    const el = track.current;
    if (!el) return;
    setIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  useEffect(() => {
    const el = track.current;
    if (!el || items.length < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Only while the carousel is actually on screen, and never while the tab
    // is in the background — a gallery that cycled unseen would be showing
    // the last slide by the time anyone scrolled to it.
    let visible = false;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.5 });
    io.observe(el);

    // "scroll" is deliberately NOT in this list: the auto-advance scrolls the
    // track itself, so listening for it would stop the timer on its own first
    // tick. A YouTube slide is an iframe, so pointerdown on it never reaches
    // us — hence also pausing whenever one is the current slide (below).
    const stop = () => { heldUntil.current = Date.now() + RESUME_AFTER_MS; };
    const events = ["pointerdown", "wheel", "touchstart", "keydown"] as const;
    events.forEach((e) => el.addEventListener(e, stop, { passive: true }));

    const id = setInterval(() => {
      if (Date.now() < heldUntil.current || !visible || document.hidden) return;
      // Hold on a video: advancing out of something that might be playing is
      // the one case where moving on is plainly wrong.
      if (youtubeId(items[Math.round(el.scrollLeft / el.clientWidth)] ?? "")) return;
      const last = el.scrollLeft >= el.scrollWidth - el.clientWidth - 2;
      el.scrollTo({ left: last ? 0 : el.scrollLeft + el.clientWidth, behavior: "smooth" });
    }, 5000);

    return () => {
      clearInterval(id);
      io.disconnect();
      events.forEach((e) => el.removeEventListener(e, stop));
    };
  }, [items]);

  if (items.length === 0) return null;
  if (items.length === 1) {
    return (
      <div className={className}>
        <Slide src={items[0]} alt={alt} eager />
      </div>
    );
  }

  return (
    <div className={`group relative ${className}`}>
      <div
        ref={track}
        onScroll={onScroll}
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((src, i) => (
          <div key={`${src}-${i}`} className="h-full w-full shrink-0 snap-center">
            <Slide src={src} alt={alt} eager={i === 0} />
          </div>
        ))}
      </div>

      {/* arrows */}
      <button
        type="button"
        aria-label="Previous"
        onClick={() => goTo(index - 1)}
        disabled={index === 0}
        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow-md transition-opacity hover:bg-white disabled:opacity-0"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      <button
        type="button"
        aria-label="Next"
        onClick={() => goTo(index + 1)}
        disabled={index === items.length - 1}
        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow-md transition-opacity hover:bg-white disabled:opacity-0"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
      </button>

      {/* dots */}
      <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-5 bg-white" : "w-1.5 bg-white/60 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function Slide({ src, alt, eager }: { src: string; alt: string; eager?: boolean }) {
  const id = youtubeId(src);
  if (id) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}`}
        title="Video"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="h-full w-full"
      />
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      className="h-full w-full object-cover [filter:contrast(1.03)_saturate(1.04)]"
    />
  );
}
